import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import qs from 'query-string';
import { connect } from 'react-redux';

import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import ErrorView from '../../../../components/global/ErrorView';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../../graphql/queries/contributors';
import { autoContributorJoin as autoContributorJoinMutation } from '../../../../graphql/mutations/contributors';
import { albumSlideshowContributeSend as albumSlideshowContributeSendMutation } from '../../../../graphql/mutations/albums';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  isAlbum,
  isMemoryBook,
  isSpecialtyAlbum,
} from '../../../../util/album';
import {
  getAutoContributorLinkHandlerRedirectMode,
  REDIRECT_MODE,
} from './helpers';
import { MeQuery } from '../../../../graphql/queries/account';
import { isAlbumFolderOwner, isAlbumOwner } from '../../../../util/account';
import AutoContributorLinkHandlerHeadMeta from './AutoContributorLinkHandlerHeadMeta';
import { ALBUM_TYPE } from '../../../../constants/albums';
import { autoContributorInvitationView as autoContributorInvitationViewMutation } from '../../../../graphql/mutations/contributor-assignments';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAlbum: PropTypes.bool,
  loadingAlbumFolder: PropTypes.bool,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  autoContributorJoin: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.any,
  }),
  authenticated: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAccount: PropTypes.bool,
  source: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingSource: PropTypes.boolean,
  autoContributorInvitationView: PropTypes.func,
};

const AutoContributorLinkHandler = ({
  album,
  albumFolder,
  loadingAlbum,
  loadingAlbumFolder,
  history,
  autoContributorJoin,
  albumSlideshowContributeSend,
  location,
  authenticated,
  account,
  loadingAccount,
  source,
  loadingSource,
  autoContributorInvitationView,
}) => {
  const entity = album || albumFolder;
  const autoContributorSettings = entity?.auto_contributor_settings;
  const {
    mode: redirectModeOverride,
    slideshow,
    mms,
    autoContributorInvitationId,
  } = qs.parse(location?.search);
  const [error, setError] = useState('');

  const redirectMode =
    redirectModeOverride ||
    getAutoContributorLinkHandlerRedirectMode(autoContributorSettings);

  const viewUrl = (() => {
    // When sharing from a specialty album, land them directly on that album
    const target =
      source?.is_video_album || isSpecialtyAlbum(source) ? source : entity;

    if (slideshow === '1' && target?.slideshow && !isMemoryBook(target)) {
      return `/albums/${target?.id}/slideshow?contribute=1`;
    }

    if ([ALBUM_TYPE.EVENT].includes(target?.album_type)) {
      const albumFolderId = isAlbum(target)
        ? target.root_album_folder.id
        : target.id;

      // In the case of source being a MSS, we redirect there.
      if (isAlbum(target) && isSpecialtyAlbum(target)) {
        return `/s/standalone-event-invite/${target.id}`;
      }

      return `/s/event-invite/${albumFolderId}`;
    }

    // Authenticated, SB users now see FWG as part of their PM
    // If the user is accessing a Shutterbug / Freemium event, however, they
    // should not access hierarchy-items, as this will be inconsistent with
    // their subsequent access (which will land them on PM instead).
    if (
      authenticated &&
      target?.is_web_gallery &&
      ![ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG].includes(target?.album_type)
    ) {
      return `/app/shutterbug/hierarchy-items/${target?.id}`;
    }

    if (
      target?.is_web_gallery &&
      ![ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG].includes(target?.album_type)
    ) {
      return isAlbum(target)
        ? `/galleries/view/album/${target?.id}`
        : `/galleries/view/folder/${target?.id}`;
    }

    return isAlbum(target)
      ? `/app/shutterbug/albums/${target?.id}`
      : `/app/shutterbug/folders/${target?.id}`;
  })();

  const uploadUrl = `/contributors/upload/${entity?.id}`;

  const autoContribute = async () => {
    if (!entity || !autoContributorSettings) {
      return;
    }

    const isOwner = isAlbum(entity)
      ? isAlbumOwner(account, entity)
      : isAlbumFolderOwner(account, entity);

    if (isOwner) {
      const redirectUrl = isAlbum(entity)
        ? `/app/shutterbug/albums/${entity?.id}`
        : `/app/shutterbug/folders/${entity?.id}`;

      history.replace(redirectUrl);
    }

    // Event types will now redirect to the new invite flow regardless of authentication.
    // Only difference being that when authed, we will try joining the user at this point.
    if ([ALBUM_TYPE.EVENT].includes(entity.album_type)) {
      if (authenticated) {
        try {
          const albumFolderId = isAlbum(entity)
            ? entity.root_album_folder.id
            : entity.id;

          await autoContributorJoin({
            id: albumFolderId ?? entity.id,
            autoContributorInvitationId,
          });

          if (mms === '1' && album) {
            albumSlideshowContributeSend(album.id);
          }
        } catch {
          // Ignore errors, as worst case scenario user will be re-joined on a per-album basis
          // during the process of uploading on the new invite flow.
        }
      }
      history.replace(viewUrl);
      return;
    }

    if (
      !authenticated &&
      entity.is_web_gallery &&
      ![ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG].includes(entity.album_type)
    ) {
      history.replace(viewUrl);
      return;
    }

    if (!authenticated) {
      setError('You do not have permissions to view this gallery.');
      return;
    }

    try {
      await autoContributorJoin({ id: entity.id, autoContributorInvitationId });

      if (redirectMode === REDIRECT_MODE.UPLOAD) {
        history.replace(uploadUrl);
      } else {
        history.replace(viewUrl);
      }
    } catch (err) {
      setError(getErrorString(err));
    }
  };

  useEffect(() => {
    if (autoContributorInvitationId) {
      autoContributorInvitationView?.(autoContributorInvitationId);
    }
  }, [autoContributorInvitationId]);

  useEffect(() => {
    if (!loadingAccount && (album || albumFolder)) {
      autoContribute();
    }
  }, [album, albumFolder, loadingAccount]);

  if (error) {
    return (
      <ErrorView
        location={{
          state: {
            error,
          },
        }}
      />
    );
  }

  if (loadingAlbum || loadingAlbumFolder || loadingAccount || loadingSource) {
    return <FullScreenLoader />;
  }

  if (!loadingAlbum && !loadingAlbumFolder && !entity) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'It looks like the link provided does not match any of our records.',
          },
        }}
      />
    );
  }

  if (entity && !autoContributorSettings) {
    return (
      <ErrorView
        location={{
          state: {
            error: "You don't have priviledges to perform this action.",
          },
        }}
      />
    );
  }

  return (
    <Fragment>
      <AutoContributorLinkHandlerHeadMeta entity={entity} />
      <FullScreenLoader />
    </Fragment>
  );
};

AutoContributorLinkHandler.propTypes = propTypes;

export { AutoContributorLinkHandler as AutoContributorLinkHandlerComp };

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match }) => ({
    variables: {
      id: match.params.entityId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    loadingAlbum: loading,
  }),
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.entityId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loadingAlbumFolder: loading,
    }),
  },
);

const withAutoContributorJoin = graphql(autoContributorJoinMutation, {
  props: ({ mutate }) => ({
    autoContributorJoin: ({ id, autoContributorInvitationId }) =>
      mutate({
        variables: {
          input: {
            id,
            auto_contributor_invitation_id: autoContributorInvitationId,
          },
        },
      }),
  }),
});

const withAlbumSlideshowContributeSend = graphql(
  albumSlideshowContributeSendMutation,
  {
    props: ({ mutate }) => ({
      albumSlideshowContributeSend: albumId =>
        mutate({ variables: { input: { album_id: albumId } } }),
    }),
  },
);

const withAutoContributorInvitationView = graphql(
  autoContributorInvitationViewMutation,
  {
    props: ({ mutate }) => ({
      autoContributorInvitationView: autoContributorInvitationId =>
        mutate({
          variables: {
            input: {
              auto_contributor_invitation_id: autoContributorInvitationId,
            },
          },
        }),
    }),
  },
);
const withUser = graphql(MeQuery, {
  props: ({ data: { me, loading } = {} }) => ({
    account: me,
    loadingAccount: loading,
  }),
  skip: ownProps => !ownProps.authenticated,
});

const mapStateToProps = ({ auth }) => {
  return {
    authenticated: auth.authenticated,
  };
};

const withAlbumAsSource = graphql(AlbumForContributorShare, {
  options: ({ location }) => ({
    variables: {
      id: qs.parse(location?.search)?.source,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    source: album,
    loadingSource: loading,
  }),
  skip: ({ location }) => !qs.parse(location?.search)?.source,
});

export default compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
  withAutoContributorJoin,
  withAlbumSlideshowContributeSend,
  withAutoContributorInvitationView,
  connect(mapStateToProps),
  withUser,
  withAlbumAsSource,
)(AutoContributorLinkHandler);
